import { Component, OnInit, input, computed, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  IonButtons,
  IonHeader,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { DistanceType } from 'src/app/enums/distance-type';
import { PermissionType } from 'src/app/enums/permission-type';
import { SettingsService } from 'src/app/services/settings.service';
import { NgTemplateOutlet } from '@angular/common';
import { IonContent, IonBackButton } from '@ionic/angular/standalone';
import { ContrastOption } from 'src/app/enums/contrast-option';
import { AppearanceOption } from 'src/app/enums/appearance-option';
import { CurrentWhiteLabelApplication } from 'src/app/utils/current-white-label-application';
import { SettingsControlComponent } from 'src/app/components/settings-control/settings-control.component';

@Component({
  standalone: true,
  selector: 'app-preferences',
  templateUrl: './preferences.page.html',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    IonContent,
    IonBackButton,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    NgTemplateOutlet,
    SettingsControlComponent,
  ],
})
export class PreferencesPage implements OnInit {
  embedded = input(false);
  public preferencesForm: FormGroup;
  public isProjectApp: boolean = false;
  public isDarkModeEnabled: boolean = false;
  readonly PermissionType = PermissionType;
  readonly DistanceType = DistanceType;
  readonly ContrastOption = ContrastOption;
  readonly AppearanceOption = AppearanceOption;

  readonly distanceOptions = computed(() =>
    Object.values(DistanceType).map((value) => ({
      value,
      labelKey:
        value === DistanceType.KM
          ? 'settings.control.distance-unit.km.full'
          : 'settings.control.distance-unit.mi.full',
      selectedLabelKey:
        value === DistanceType.KM
          ? 'settings.control.distance-unit.km'
          : 'settings.control.distance-unit.mi',
    }))
  );

  readonly contrastOptions = computed(() =>
    Object.values(ContrastOption).map((value) => ({
      value,
      labelKey: `settings.control.contrast.${value}`,
    }))
  );

  readonly appearanceOptions = computed(() =>
    Object.values(AppearanceOption).map((value) => ({
      value: value as string,
      labelKey: `settings.control.appearance.${value}`,
    }))
  );

  private readonly fb = inject(FormBuilder);
  private settingsService = inject(SettingsService);
  private app = inject(CurrentWhiteLabelApplication);

  constructor() {
    this.isProjectApp = this.app.isProjectApp();
    this.isDarkModeEnabled = this.app.isDarkModeEnabled();
    this.createPreferencesForm();
  }

  async ngOnInit() {
    let suggestionsEnabled = await this.settingsService.getSuggestionsEnabled();
    if (suggestionsEnabled === null) suggestionsEnabled = true;
    let distanceType = await this.settingsService.getDistanceType();
    let appearance = await this.settingsService.getAppearance();
    if (appearance === null) appearance = AppearanceOption.SYSTEM;
    let contrast = await this.settingsService.getContrast();
    if (contrast === null) contrast = ContrastOption.SYSTEM;
    this.preferencesForm.patchValue(
      {
        suggestionsEnabled: suggestionsEnabled,
        distanceType: distanceType,
        appearance: appearance,
        contrast: contrast,
      },
      { emitEvent: false }
    );
  }

  get suggestionsControl(): FormControl<boolean> {
    return this.preferencesForm.controls
      .suggestionsEnabled as FormControl<boolean>;
  }

  get distanceTypeControl(): FormControl<DistanceType> {
    return this.preferencesForm.controls
      .distanceType as FormControl<DistanceType>;
  }

  get contrastControl(): FormControl<ContrastOption> {
    return this.preferencesForm.controls
      .contrast as FormControl<ContrastOption>;
  }

  get appearanceControl(): FormControl<AppearanceOption> {
    return this.preferencesForm.controls
      .appearance as FormControl<AppearanceOption>;
  }

  createPreferencesForm() {
    this.preferencesForm = this.fb.group({
      suggestionsEnabled: [],
      distanceType: [],
      appearance: [],
      contrast: [],
    });

    this.preferencesForm
      .get('suggestionsEnabled')
      .valueChanges.subscribe((value) => {
        this.settingsService.setSuggestionsEnabled(value).then(() => {
          this.settingsService.settingsChanged$.next(true);
        });
      });

    this.preferencesForm.get('distanceType').valueChanges.subscribe((value) => {
      this.settingsService.saveDistanceType(value).then(() => {
        this.settingsService.settingsChanged$.next(true);
      });
    });

    this.preferencesForm.get('appearance').valueChanges.subscribe((value) => {
      this.settingsService.saveAppearance(value);
    });

    this.preferencesForm.get('contrast').valueChanges.subscribe((value) => {
      this.settingsService.saveContrast(value).then(() => {
        this.settingsService.checkContrast();
      });
    });
  }
}
